import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import UnifiedNavHeader from "../../../../components/navigation/UnifiedNavHeader";
import MobileSportsBar from "../../../../components/homePagePanel/MobileSportsBar";
import RailNavigation from "../../../../components/dashboards/mobile/RailNavigation";
import { DatePickerSheet } from "../../../../components/dashboards/components/shared/date-picker";
import MMAMobileFightCard from "../components/MMAMobileFightCard";
import MobileMMAPredictionSheet from "../components/mobile/MobileMMAPredictionSheet";
import useMMARailFilters from "../components/MMARailFilters";
import { useQuickPrediction } from "../../../../components/dashboards/hooks/useQuickPrediction";
import mmaService from "../../../../components/dashboards/services/mmaService";
import { Header } from "../../../../components/dashboards/components/shared/Header";
import { MMA_HEADER_COPY } from "../constants/headerContent";
import { getBrazilianStatus } from "../../../../utils/detectBrazil";
import { mmaMobilePtBR } from "../../../../components/mappings/sports-dash-mappings/mma-mobile.pt-br";
import { ensureISODate } from "../../../../components/dashboards/utils/dateUtils";

const DEFAULT_PAGINATION = { currentPage: 1, perPage: 5, totalPages: 1 };

const MMAMobileDashboard = () => {
  const isBrazilian = getBrazilianStatus();
  const t = useCallback(
    (text) => (isBrazilian ? mmaMobilePtBR[text] || text : text),
    [isBrazilian]
  );

  const [fights, setFights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [selectedFight, setSelectedFight] = useState(null);
  const [showPredictionSheet, setShowPredictionSheet] = useState(false);
  const [selectedFightForPrediction, setSelectedFightForPrediction] = useState(null);
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [predictionRequestId, setPredictionRequestId] = useState(0);
  const activePredictionRequestRef = useRef(null);
  const [predictionFlowActive, setPredictionFlowActive] = useState(false);
  const [sheetSelectionCount, setSheetSelectionCount] = useState(0);
  const cacheCheckKeyRef = useRef(null);

  // Prediction hook
  const {
    isLoading: isPredicting,
    selectedGames,
    usageState,
    error: predictionError,
    toggleGameSelection,
    clearSelections,
    predictSelectedGames,
    getGamePrediction,
    isGameSelected,
    selectionCount,
    isGameFrozen,
    checkCachedPredictions
  } = useQuickPrediction();

  const loadFights = useCallback(async (dateVal) => {
    try {
      setLoading(true);
      setError(null);
      const isoDate = ensureISODate(dateVal);
      const data = await mmaService.makeRequest('/fights', { date: isoDate });
      const list = data?.response || data?.fights || [];
      setFights(list);
      setPagination(prev => ({
        ...prev,
        totalPages: Math.max(1, Math.ceil(list.length / prev.perPage)),
      }));
    } catch (e) {
      setError('Failed to load fights');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRailFiltersChange = useCallback((nextFilters) => {
    const iso = ensureISODate(nextFilters.date);
    loadFights(iso);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [loadFights]);

  const {
    filters: railFilters,
    filterObjects: railFilterObjects,
    activeTab: railActiveTab,
    handleTabChange: handleRailTabChange,
    handleFilterClick,
    updateFilter: updateRailFilter,
    showDateSheet,
    setShowDateSheet
  } = useMMARailFilters({ initialDate: new Date().toISOString().split('T')[0], onFiltersChange: handleRailFiltersChange });



  useEffect(() => {
    loadFights(ensureISODate(railFilters.date));
  }, [loadFights, railFilters.date]);

  // Prime predictions from cache/Firestore when fights load
  useEffect(() => {
    if (!fights.length) return;
    const fightIds = fights.map((f) => f.id).filter(Boolean);
    if (!fightIds.length) return;
    const key = fightIds.slice().sort().join(",");
    if (cacheCheckKeyRef.current === key) return;
    cacheCheckKeyRef.current = key;

    checkCachedPredictions?.("mma", fightIds).catch((err) =>
      console.error("checkCachedPredictions failed (MMA mobile):", err)
    );
  }, [fights, checkCachedPredictions]);

  const handleOpenPredictionSheet = useCallback(
    (fight) => {
      if (!fight) return;
      const fightPrediction = getGamePrediction(fight.id);
      setSelectedFightForPrediction(fight);
      setSelectedPrediction(fightPrediction);
      setShowPredictionSheet(true);
      setSheetSelectionCount(selectionCount);
    },
    [getGamePrediction, selectionCount]
  );

  const handleClosePredictionSheet = useCallback(() => {
    setShowPredictionSheet(false);
    setSelectedFightForPrediction(null);
    setSelectedPrediction(null);
    setSheetSelectionCount(0);
  }, []);

  const handlePredictRequest = useCallback(async () => {
    setPredictionRequestId((prev) => {
      const next = prev + 1;
      activePredictionRequestRef.current = next;
      return next;
    });
    setPredictionFlowActive(true);
    await predictSelectedGames("mma");
    setPredictionFlowActive(false);
  }, [predictSelectedGames]);

  useEffect(() => {
    if (showPredictionSheet && selectedFightForPrediction) {
      const updatedPrediction = getGamePrediction(selectedFightForPrediction.id);
      setSelectedPrediction(updatedPrediction);
    }
  }, [showPredictionSheet, selectedFightForPrediction, getGamePrediction]);

  // Keep local sheet count in sync while the sheet is open
  useEffect(() => {
    if (showPredictionSheet) {
      setSheetSelectionCount(selectionCount);
    }
  }, [selectionCount, showPredictionSheet]);

  useEffect(() => {
    if (isPredicting) return;

    const shouldFinalize =
      !isPredicting &&
      !predictionFlowActive &&
      activePredictionRequestRef.current === predictionRequestId &&
      predictionRequestId > 0;

    if (shouldFinalize) {
      clearSelections();
      handleClosePredictionSheet();
      activePredictionRequestRef.current = null;
      setTimeout(() => clearSelections(), 600);
      setSheetSelectionCount(0);
    }
  }, [
    clearSelections,
    handleClosePredictionSheet,
    isPredicting,
    predictionFlowActive,
    predictionRequestId
  ]);

  const paginatedFights = useMemo(() => {
    const start = (pagination.currentPage - 1) * pagination.perPage;
    return fights.slice(start, start + pagination.perPage);
  }, [fights, pagination]);

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, currentPage: Math.max(1, Math.min(page, prev.totalPages)) }));
  };

  return (
    <div className="min-h-screen bg-[#050311] text-white">
      <UnifiedNavHeader title="MMA Dashboard" showBackButton />

      {/* Mobile Sports Navigation Bar */}
      <MobileSportsBar />

      <div className="px-4 py-6 pb-24">
        <Header t={t} {...MMA_HEADER_COPY} />
        <div className="mt-4 mb-4">
          <RailNavigation
            sport="mma"
            primaryTabs={[{ id: 'all', label: 'All Fights' }]}
            filters={railFilterObjects}
            activeTab={railActiveTab}
            onTabChange={handleRailTabChange}
            onFilterChange={handleFilterClick}
            onReset={() => updateRailFilter('date', new Date().toISOString().split('T')[0])}
            hasActiveFilters={false}
            className="border-y border-white/10 bg-[#050311]/95 backdrop-blur"
          />
        </div>

        <section className="border border-white/10 bg-white/[0.02]">
          <div className="flex items-center justify-between border-b border-white/5 px-4 py-3">
            <div>
              <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-gray-200">Currently showing</p>
              <h2 className="text-lg font-black uppercase tracking-tight text-white">Fights ({fights.length})</h2>
            </div>
            <span className="text-[11px] font-mono uppercase tracking-[0.3em] text-gray-200">Page {pagination.currentPage} of {pagination.totalPages}</span>
          </div>

          <div className="p-4 space-y-3">
            {loading ? (
              <div className="text-sm text-white/70">Loading fights...</div>
            ) : error ? (
              <div className="text-sm text-red-400">{error}</div>
            ) : paginatedFights.length === 0 ? (
              <div className="text-sm text-white/60">No fights found</div>
            ) : (
              paginatedFights.map((fight, idx) => (
                <MMAMobileFightCard
                  key={fight?.id || idx}
                  fight={fight}
                  isSelected={isGameSelected(fight)}
                  isFrozen={isGameFrozen(fight.id)}
                  onToggleSelection={toggleGameSelection}
                  prediction={getGamePrediction(fight.id)}
                  onViewPrediction={handleOpenPredictionSheet}
                />
              ))
            )}
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 border-t border-white/5 px-4 py-3">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="flex items-center gap-1 border border-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-200 transition-all disabled:opacity-40"
              >
                Prev
              </button>
              <span className="text-xs font-mono uppercase tracking-[0.3em] text-gray-200">
                {pagination.currentPage} / {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="flex items-center gap-1 border border-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-200 transition-all disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </section>
      </div>

      <DatePickerSheet
        isOpen={showDateSheet}
        onClose={() => setShowDateSheet(false)}
        selectedDate={ensureISODate(railFilters.date)}
        onDateSelect={(dateValue) => {
          const iso = ensureISODate(dateValue);
          updateRailFilter("date", iso);
          setShowDateSheet(false);
        }}
      />

      <MobileMMAPredictionSheet
        isOpen={showPredictionSheet}
        fight={selectedFightForPrediction}
        prediction={selectedPrediction}
        selectionCount={sheetSelectionCount}
        usageState={usageState}
        isLoading={isPredicting}
        onPredict={handlePredictRequest}
        onClear={clearSelections}
        onClose={handleClosePredictionSheet}
        t={t}
      />

    </div>
  );
};

export default MMAMobileDashboard;
